/**
 * @file Entree tactile/souris/clavier -> direction de deplacement ECRAN (joystick flottant FIXE : le point de pose du doigt devient le centre et n'en bouge plus).
 */

/** Etat d'entree en coordonnees ECRAN (x droite, y BAS ; norme 0..1). app/InputBridge le convertit en monde. */
export interface ScreenInput {
  x: number;
  y: number;
  active: boolean;
  /** Centre et position courante du joystick flottant (px CSS) pour l'affichage (ui/hud/Joystick). */
  originX: number;
  originY: number;
  knobX: number;
  knobY: number;
}

export interface PointerInputOptions {
  /** Rayon du joystick (px CSS) : au-dela, la norme sature a 1. */
  radiusPx: number;
  /** Zone morte (fraction du rayon). */
  deadZone: number;
  /** Les pointeurs qui demarrent sur ces elements (boutons UI) sont ignores. */
  ignoreSelector: string;
}

/** Touches de direction (par position physique `KeyboardEvent.code` : WASD sur QWERTY = ZQSD sur AZERTY) -> axe ecran. */
const KEY_AXES: Readonly<Record<string, readonly [number, number]>> = {
  KeyW: [0, -1],
  ArrowUp: [0, -1],
  KeyS: [0, 1],
  ArrowDown: [0, 1],
  KeyA: [-1, 0],
  ArrowLeft: [-1, 0],
  KeyD: [1, 0],
  ArrowRight: [1, 0],
};

const isTypingTarget = (t: EventTarget | null): boolean => {
  if (!(t instanceof HTMLElement)) return false;
  return t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT';
};

export class PointerInput {
  readonly state: ScreenInput = { x: 0, y: 0, active: false, originX: 0, originY: 0, knobX: 0, knobY: 0 };
  private pointerId: number | null = null;
  private readonly keys = new Set<string>();

  constructor(
    private readonly surface: HTMLElement,
    private readonly opts: PointerInputOptions = { radiusPx: 64, deadZone: 0.12, ignoreSelector: '[data-ui-block]' },
  ) {}

  /**
   * Branche pointerdown/pointermove/pointerup/pointercancel sur `surface` (passive: false, preventDefault pour bloquer le
   * scroll iOS) + clavier sur window. Coordonnees d'affichage RELATIVES a la surface (le joystick de l'UI est positionne dans
   * le conteneur du jeu). Retourne la fonction de nettoyage (removeEventListener), appelee par App.dispose().
   */
  attach(): () => void {
    const surface = this.surface;
    const { radiusPx, deadZone, ignoreSelector } = this.opts;
    const prevTouchAction = surface.style.touchAction;
    surface.style.touchAction = 'none';

    // Rectangle de la surface mis en cache au pointerdown : getBoundingClientRect a chaque pointermove forcait un recalcul de
    // mise en page (le HUD change tous les frames) et ralentissait le joystick sur telephone.
    let rect = { left: 0, top: 0 };
    const rel = (e: PointerEvent): { x: number; y: number } => ({ x: e.clientX - rect.left, y: e.clientY - rect.top });

    const release = (): void => {
      this.pointerId = null;
      this.refreshFromKeys();
    };

    const onDown = (e: PointerEvent): void => {
      if (this.pointerId !== null) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      const target = e.target;
      if (target instanceof Element && target.closest(ignoreSelector)) return;
      if (e.cancelable) e.preventDefault();
      this.pointerId = e.pointerId;
      const r = surface.getBoundingClientRect();
      rect = { left: r.left, top: r.top };
      const p = rel(e);
      const s = this.state;
      s.active = true;
      s.x = 0;
      s.y = 0;
      s.originX = p.x;
      s.originY = p.y;
      s.knobX = p.x;
      s.knobY = p.y;
      try {
        surface.setPointerCapture(e.pointerId);
      } catch {
        // pointeur synthetique / deja libere : sans consequence
      }
    };

    const onMove = (e: PointerEvent): void => {
      if (e.pointerId !== this.pointerId) return;
      if (e.cancelable) e.preventDefault();
      const p = rel(e);
      const s = this.state;
      // Le joystick reste FIXE a l'endroit ou le doigt s'est pose : seul le bouton bouge, borne au rayon (la norme sature a 1).
      let dx = p.x - s.originX;
      let dy = p.y - s.originY;
      const len = Math.hypot(dx, dy);
      if (len > radiusPx) {
        const k = radiusPx / len;
        dx *= k;
        dy *= k;
      }
      const nx = dx / radiusPx;
      const ny = dy / radiusPx;
      const n = Math.hypot(nx, ny);
      if (n < deadZone) {
        s.x = 0;
        s.y = 0;
      } else {
        const sc = n > 1 ? 1 / n : 1;
        s.x = nx * sc;
        s.y = ny * sc;
      }
      s.knobX = s.originX + dx;
      s.knobY = s.originY + dy;
    };

    const onUp = (e: PointerEvent): void => {
      if (e.pointerId !== this.pointerId) return;
      try {
        surface.releasePointerCapture(e.pointerId);
      } catch {
        // deja libere
      }
      release();
    };

    const onLostCapture = (e: PointerEvent): void => {
      if (e.pointerId === this.pointerId) release();
    };

    const onTouchMove = (e: TouchEvent): void => {
      // iOS : empeche le defilement / rebond de la page pendant le glissement du joystick
      if (this.pointerId !== null && e.cancelable) e.preventDefault();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (!(e.code in KEY_AXES) || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return;
      e.preventDefault();
      this.keys.add(e.code);
      if (this.pointerId === null) this.refreshFromKeys();
    };

    const onKeyUp = (e: KeyboardEvent): void => {
      if (!this.keys.delete(e.code)) return;
      if (this.pointerId === null) this.refreshFromKeys();
    };

    const onBlur = (): void => {
      this.keys.clear();
      release();
    };

    const passiveFalse = { passive: false } as const;
    surface.addEventListener('pointerdown', onDown, passiveFalse);
    surface.addEventListener('pointermove', onMove, passiveFalse);
    surface.addEventListener('pointerup', onUp, passiveFalse);
    surface.addEventListener('pointercancel', onUp, passiveFalse);
    surface.addEventListener('lostpointercapture', onLostCapture);
    surface.addEventListener('touchmove', onTouchMove, passiveFalse);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);

    return () => {
      surface.removeEventListener('pointerdown', onDown);
      surface.removeEventListener('pointermove', onMove);
      surface.removeEventListener('pointerup', onUp);
      surface.removeEventListener('pointercancel', onUp);
      surface.removeEventListener('lostpointercapture', onLostCapture);
      surface.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      surface.style.touchAction = prevTouchAction;
      this.keys.clear();
      this.pointerId = null;
      this.reset();
    };
  }

  private reset(): void {
    const s = this.state;
    s.active = false;
    s.x = 0;
    s.y = 0;
  }

  /** Clavier : vecteur normalise (diagonales a norme 1), joystick affiche au centre de la surface tant qu'une touche est tenue. */
  private refreshFromKeys(): void {
    if (this.keys.size === 0) {
      this.reset();
      return;
    }
    let x = 0;
    let y = 0;
    for (const code of this.keys) {
      const axis = KEY_AXES[code];
      if (!axis) continue;
      x += axis[0];
      y += axis[1];
    }
    const n = Math.hypot(x, y);
    if (n > 0) {
      x /= n;
      y /= n;
    }
    const r = this.surface.getBoundingClientRect();
    const s = this.state;
    s.active = true;
    s.x = x;
    s.y = y;
    s.originX = r.width / 2;
    s.originY = r.height / 2;
    s.knobX = s.originX + x * this.opts.radiusPx;
    s.knobY = s.originY + y * this.opts.radiusPx;
  }
}
